import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  ITracker,
  IFilter,
  IQueryParam,
  IPopulate,
  IReporteBase,
  IReporteGenerico,
  IListado,
  IDispositivoLorawan,
} from 'modelos/src';
import { Subscription } from 'rxjs';

import { TrackersService } from '../trackers.service';
import { MatDialog } from '@angular/material/dialog';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { CrearEditarTrackerComponent } from '../crear-editar-tracker/crear-editar-tracker.component';
import { DatePipe } from '@angular/common';
import { DarAltaTrackerComponent } from '../dar-alta-tracker/dar-alta-tracker.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { SimuladorTrackerComponent } from '../simulador-tracker/simulador-tracker.component';
import { CrearEditarEstadoCuentaComponent } from '../../estado-entidad/crear-editar-estado-cuenta/crear-editar-estado-cuenta.component';
import {
  CrearEditarEventoTecnicoGenericoComponent,
  ICrearEditarEventoTecnicoGenericoData,
} from '../../../eventos-genericos/eventos-tecnicos-genericos/crear-editar/crear-editar-evento-tecnico-generico.component';
import { LoginService } from '../../../../login/login.service';
// Dueno de `htmlSenial`: las barras de senal de los T1000-B se dibujan con
// el mismo helper que el listado de dispositivos lorawan, para que la
// columna se vea igual en las dos pantallas.
import { LuminariasService } from '../../../luminarias/service';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import {
  estadoConexionCelda,
  estadoHtml,
  fechaCelda,
  haceCelda,
} from '../../../../../auxiliares/tabla/celdas';
import { TonoEstado } from '../../../../../auxiliares/tabla/tabla.interfaces';

interface ITrackerTabla extends ITracker {
  ultimoReporte?: IReporteBase<
    'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
  >;
}

@Component({
  selector: 'app-trackers',
  templateUrl: './trackers.component.html',
  styleUrl: './trackers.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListadoTrackersComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(TrackersService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  paramsService = inject(ParamsService);
  private cdr = inject(ChangeDetectorRef);
  private luminariasService = inject(LuminariasService);

  //// Tabla nueva
  public name = 'ListadoTrackersComponent';

  public datos?: ITrackerTabla[];
  public totalCount = 0;
  public columnas?: IColumnas<ITrackerTabla>[];
  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Administración - Crear trackers'),
    tooltip: 'Crear tracker',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  // Suscripciones
  public trackers$?: Subscription;
  public reportes$?: Subscription;

  public search: IRegExpSearch = {
    fields: ['uniqueId', 'nombre', 'identificacion'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public tipo: IFiltroTabla = {
    elementos: ['GPRS', 'Qualcomm', 'T1000-B'],
    filter: {
      field: 'tipo',
    },
    label: 'Tipo',
    tipo: 'ngselect',
  };
  // ── Filtros LoRaWAN ──────────────────────────────────────────────────────
  // Solo tienen sentido para los T1000-B (el resto de los trackers no reporta
  // por LoRa), pero no filtran por tipo a propósito: combinarlos con el filtro
  // "Tipo" es lo natural, y así no se duplica el criterio.
  // Copiados del listado de dispositivos LoRaWAN, que ya los tenía armados.
  public ultimaComunicacionLora: IFiltroTabla = {
    elementos: [
      {
        label: 'Últimas 24 horas',
        filter: [
          {
            't1000b.fechaUltimaComunicacion': {
              $gte: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            },
          },
        ],
      },
      {
        label: 'Más de 1 día',
        filter: [
          {
            't1000b.fechaUltimaComunicacion': {
              $lt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            },
          },
        ],
      },
      {
        label: 'Más de 1 semana',
        filter: [
          {
            't1000b.fechaUltimaComunicacion': {
              $lt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            },
          },
        ],
      },
    ],
    filter: { field: '$or' },
    label: 'Última comunicación (LoRa)',
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'filter',
  };

  // Tasa de entrega = 100 - paquetes.perdidaPaquetes.
  public tasaEntrega: IFiltroTabla = {
    elementos: [
      {
        label: 'Excelente (≥ 95%)',
        filter: [{ 't1000b.paquetes.perdidaPaquetes': { $lt: 5 } }],
      },
      {
        label: 'Aceptable (80% – 95%)',
        filter: [{ 't1000b.paquetes.perdidaPaquetes': { $gte: 5, $lt: 20 } }],
      },
      {
        label: 'Crítico (< 80%)',
        filter: [{ 't1000b.paquetes.perdidaPaquetes': { $gte: 20 } }],
      },
    ],
    filter: { field: '$or' },
    label: 'Tasa de entrega (LoRa)',
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'filter',
  };

  public asignado: IFiltroTabla = {
    elementos: [
      { label: 'Sí', value: [{ asignadoA: { $ne: null } }] },
      { label: 'No', value: [{ asignadoA: { $eq: null } }] },
    ],
    filter: {
      field: '$and',
    },
    label: 'Asignado',
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'value',
  };

  //Alta o no alta
  private Alta: IFiltroTabla = {
    elementos: [
      {
        label: 'Dado de alta',
        value: [{ fechaAlta: { $exists: true, $ne: null } }],
      },
      { label: 'Sin alta', value: [{ fechaAlta: null }] },
    ],
    filter: {
      field: '$or',
    },
    label: 'Alta',
    rapido: true,
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'value',
  };

  //Fecha de alta
  private fechaAlta: IFiltroTabla = {
    filter: {
      field: 'fechaAlta',
    },
    label: 'Buscar fecha de alta',
    tipo: 'dateRange',
  };

  //Estado de la cuenta
  private habilitado: IFiltroTabla = {
    elementos: [
      {
        label: 'Habilitado',
        icon: 'check',
        iconColor: 'var(--color-green-text)',
        filter: [{ estadoCuenta: null }, { estadoCuenta: 'Habilitado' }],
      },
      {
        label: 'Suspendido',
        icon: 'block',
        iconColor: 'var(--color-red-text)',
        filter: [{ estadoCuenta: 'Suspendido' }],
      },
    ],
    filter: {
      field: '$or',
    },
    label: 'Habilitación',
    rapido: true,
    tipo: 'ngselect',
    selectIcon: true,
    selectLabel: 'label',
    selectValue: 'filter',
  };

  private modelo: IFiltroTabla = {
    filter: { field: 'idModelo' },
    label: 'Modelo',
    tipo: 'modelo-dispositivo-selector',
    tipoDispositivo: 'Tracker',
  };

  public filtros: IFiltroTabla[] = [
    this.tipo,
    this.asignado,
    this.Alta,
    this.fechaAlta,
    this.habilitado,
    this.modelo,
    this.ultimaComunicacionLora,
    this.tasaEntrega,
  ];

  public filter: IFilter<ITracker> = {};
  public populate: IPopulate = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    { path: 'modelo' },
    {
      path: 'serviciosContratados',
      select: 'nombre',
    },

    {
      path: 'serviciosContratados',
      select: 'nombre icono',
    },

    {
      path: 'activo',
      select: 'categoria identificacion',
    },
    {
      path: 'sim1',
    },
    {
      path: 'sim2',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  // Listados para filtros

  // Listados

  public listar(): void {
    this.trackers$?.unsubscribe();
    this.reportes$?.unsubscribe();

    // Si quedó guardado un sort por ultimaComunicacion, resetearlo
    if (this.query.sort?.includes('ultimaComunicacion')) {
      this.query.sort = 'nombre';
    }

    this.trackers$ = this.lista2
      .trackers(this.query, 30)
      .subscribe(async (data) => {
        await this.completaUltimoReporte(data);
      });
  }

  public async completaUltimoReporte(
    listado: IListado<ITrackerTabla>,
  ): Promise<void> {
    if (!listado.datos.length) {
      this.datos = [];
      this.totalCount = 0;
      this.cdr.markForCheck();
      return;
    }

    const idsTrackers = listado.datos.map((t) => t._id);
    const filter: IFilter<IReporteGenerico> = {
      idEntidad: { $in: idsTrackers },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };

    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<
        'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
      >(query)
      .subscribe((data) => {
        for (const tracker of listado.datos) {
          tracker.ultimoReporte = data.datos.find(
            (r) => r.idEntidad === tracker._id,
          );
        }

        if (HelperService.cambiaronLosDatos(this.datos, listado.datos)) {
          this.totalCount = listado.totalCount;
          this.datos = listado.datos;
          this.cdr.markForCheck();
        }
      });
  }

  // Acciones
  public async crear() {
    this.paramsService.setParams({});
    this.formularios
      .abrir(CrearEditarTrackerComponent, { tamano: 'grande' })
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.listar();
        }
      });
  }

  public async editar(tracker: ITracker) {
    this.paramsService.setParams({ tracker: tracker });
    this.formularios
      .abrir(CrearEditarTrackerComponent, { tamano: 'grande' })
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.listar();
        }
      });
  }

  public detalles(data: ITracker) {
    this.navigator.navigate(['detallesTracker', data._id], {
      relativeTo: this.route,
    });
  }

  public async darAlta(tracker: ITracker) {
    this.formularios
      .abrir(DarAltaTrackerComponent, {
        tamano: 'mediano',
        data: { edit: tracker },
      })
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.listar();
        }
      });
  }

  public async actualizarImagenes(tracker: ITracker) {
    this.formularios
      .abrir(DarAltaTrackerComponent, {
        tamano: 'mediano',
        data: { edit: tracker },
      })
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.listar();
        }
      });
  }

  public async eliminar(tracker: ITracker) {
    // En un T1000-B se ofrece borrar también el device en ChirpStack, igual que
    // en dispositivos LoRaWAN. El resto de los trackers no existe allá.
    if (tracker.tipo === 'T1000-B') {
      const conf = await this.dialogService.confirm(
        'Eliminar Tracker',
        `¿Querés eliminar el tracker ${tracker.nombre}?`,
        'Aceptar',
        'Cancelar',
        true,
        'Eliminar también en LoRaWAN Server',
        true,
      );
      if (!conf.confirmed) return;
      await this.borrarTracker(tracker, conf.checkbox);
      return;
    }

    const res = await this.dialogService.confirm(
      'Eliminar Tracker',
      `¿Querés eliminar el tracker ${tracker.nombre}?`,
    );
    if (!res) return;
    await this.borrarTracker(tracker, true);
  }

  private async borrarTracker(
    tracker: ITracker,
    eliminarServer: boolean,
  ): Promise<void> {
    try {
      await this.service.delete(tracker._id!, eliminarServer);
      await this.listar();
      this.helper.notifSuccess('Tracker Eliminado');
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // ── Network Server (ChirpStack) ─────────────────────────────────────────
  // Análogos a los de DispositivosLorawanComponent. Solo tienen sentido para
  // T1000-B; el submenú que los expone está oculto para el resto de los tipos.

  public async consultarDeviceChirpstack(tracker: ITracker) {
    const res = await this.dialogService.confirm(
      'Consultar',
      `¿Querés consultar si existe el tracker ${tracker.nombre} en Network Server?`,
    );
    if (!res) return;
    try {
      const device = await this.service.consultarChirpstack(tracker._id!);
      if (device?.device?.devEui) {
        this.helper.notifSuccess('Dispositivo encontrado en Network Server');
      } else {
        this.helper.notifError('Dispositivo no encontrado en Network Server');
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  public async crearChirpstack(tracker: ITracker) {
    const res = await this.dialogService.confirm(
      'Crear',
      `¿Querés crear el tracker ${tracker.nombre} en Network Server?`,
    );
    if (!res) return;
    try {
      await this.service.createChirpstack(tracker);
      this.helper.notifSuccess('Tracker creado en Network Server');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  public async eliminarChirpstack(tracker: ITracker) {
    const res = await this.dialogService.confirm(
      'Eliminar',
      `¿Querés eliminar el tracker ${tracker.nombre} en Network Server?`,
    );
    if (!res) return;
    try {
      await this.service.deleteChirpstack(tracker._id!);
      this.helper.notifSuccess('Tracker eliminado de Network Server');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  public async crearEstadoEntidad(tracker: ITracker) {
    this.formularios
      .abrir(CrearEditarEstadoCuentaComponent, {
        tamano: 'chico',
        data: { tracker },
      })
      .afterClosed()
      .subscribe(async (res) => {
        if (res) {
          await this.listar();
        }
      });
  }

  public crearEventoTecnico(tracker: ITracker) {
    const data: ICrearEditarEventoTecnicoGenericoData = {
      idEntidad: tracker._id!,
      tipoEvento: 'Evento Técnico Tracker',
      categoria: 'Tracker',
      idsCQAET: [LoginService.getCliente()?._id!],
    };
    this.formularios.abrir(CrearEditarEventoTecnicoGenericoComponent, {
      tamano: 'grande',
      data,
    });
  }

  public simularTracker(tracker: ITracker) {
    this.paramsService.setParams({ trackerId: tracker._id });
    this.formularios.abrir(SimuladorTrackerComponent, { tamano: 'grande' });
  }

  /**
   * Genera el HTML para mostrar los servicios como iconos
   */
  private getServiciosHtml(dato: ITracker): string {
    if (!dato.serviciosContratados || dato.serviciosContratados.length === 0) {
      return '';
    }

    const iconosHtml = dato.serviciosContratados
      .map((s) => {
        if (s.icono) {
          return `<img src="${escaparHtml(s.icono)}" alt="${escaparHtml(s.nombre)}" title="${escaparHtml(s.nombre)}" class="row-icono-img" />`;
        }
        // Si no tiene icono, mostrar inicial del nombre
        const inicial = s.nombre?.charAt(0)?.toUpperCase() || '?';
        return `<span class="tabla-codigo" title="${escaparHtml(s.nombre)}">${escaparHtml(inicial)}</span>`;
      })
      .join('');

    return `<div class="tabla-celda-fila">${iconosHtml}</div>`;
  }

  private orientacionAbreviada(orientacion?: string): string {
    switch (orientacion) {
      case 'Norte':
        return 'N';
      case 'Noreste':
        return 'NE';
      case 'Este':
        return 'E';
      case 'Sureste':
        return 'SE';
      case 'Sur':
        return 'S';
      case 'Suroeste':
        return 'SO';
      case 'Oeste':
        return 'O';
      case 'Noroeste':
        return 'NO';
      default:
        return '';
    }
  }

  // Tabla

  /** Dato con su ícono de entidad (clase común de la tabla). */
  private linea(texto: string, icono: string, clase = ''): string {
    return `<div class="tabla-linea"><span class="material-symbols-outlined">${icono}</span><span class="${clase}">${texto}</span></div>`;
  }

  /**
   * Estado de radio de un T1000-B: barras de señal y tasa de entrega, como
   * bloques sueltos para que la columna los intercale en su orden. Devuelve
   * [] para el resto de los tipos, que no son LoRaWAN.
   *
   * Vive dentro de la columna "Última comunicación" y no en una propia porque
   * de los cuatro tipos de tracker sólo el T1000-B es LoRa: una columna aparte
   * quedaba vacía en la mayoría de las filas. Reusa `htmlSenial` y el mismo
   * estado de tasa que el listado de dispositivos LoRaWAN.
   *
   * NO repite `fechaUltimaComunicacion`: es el mismo instante que ya muestra
   * el reporte de arriba, con otro formato.
   */
  private htmlConectividadLora(dato: ITrackerTabla): string[] {
    if (dato.tipo !== 'T1000-B') return [];
    const lora = dato.t1000b;
    const partes: string[] = [];

    if (lora?.margin != null) {
      // htmlSenial solo lee `margin`; el resto del dispositivo no le importa.
      partes.push(
        `<div class="tabla-celda-fila">${this.luminariasService.htmlSenial({
          margin: lora.margin,
        } as IDispositivoLorawan)}</div>`,
      );
    }

    const perdida = lora?.paquetes?.perdidaPaquetes;
    if (perdida != null) {
      const tasa = Math.max(0, Math.min(100, 100 - perdida));
      // Mismos cortes y tonos que la columna "Tasa de entrega" de
      // dispositivos-lorawan.
      const [tono, icono]: [TonoEstado, string] =
        tasa >= 95
          ? ['ok', 'check_circle']
          : tasa >= 80
            ? ['aviso', 'warning']
            : ['error', 'error'];
      partes.push(
        `<span>${estadoHtml({ texto: `${tasa.toLocaleString('es-AR', { maximumFractionDigits: 1 })} %`, tono, icono })}</span>`,
      );
    }

    return partes;
  }

  /**
   * Detalle LoRaWAN para el tooltip de "Última comunicación": devEUI, señal y
   * calidad del enlace, con el mismo vocabulario que dispositivos-lorawan.
   */
  private tooltipConectividadLora(dato: ITrackerTabla): string {
    if (dato.tipo !== 'T1000-B') return '';
    const lora = dato.t1000b;
    const items: string[] = [];

    if (lora?.deveui) {
      items.push(
        `<div>DevEUI: <strong>${escaparHtml(lora.deveui)}</strong></div>`,
      );
    }
    if (lora?.margin != null) {
      items.push(
        `<div>Señal: <strong>${Math.round(lora.margin)} dB</strong></div>`,
      );
    }

    const perdida = lora?.paquetes?.perdidaPaquetes;
    if (perdida != null) {
      const tasa = Math.max(0, Math.min(100, 100 - perdida));
      const recibidos = lora.paquetes?.framesRecibidos || 0;
      // Misma fórmula que el backend: el fCnt inicial es el del primer frame
      // de la sesión, así que los esperados son el delta más uno.
      const esperados =
        (lora.paquetes?.ultimoFcnt || 0) -
        (lora.paquetes?.fCntInicial || 0) +
        1;
      const estado =
        tasa >= 95 ? 'Excelente' : tasa >= 80 ? 'Aceptable' : 'Crítico';
      items.push(
        `<div>${estado}: <strong>${tasa.toLocaleString('es-AR', { maximumFractionDigits: 1 })} %</strong> entregados (${recibidos.toLocaleString('es-AR')} recibidos, ${esperados.toLocaleString('es-AR')} esperados)</div>`,
      );
    }

    return items.join('');
  }

  private setColumnas() {
    const columnas: IColumnas<ITrackerTabla>[] = [
      //Nombre e identificación
      {
        header: {
          label: 'Tracker',
          sort: 'nombre',
          sortable: true,
        },
        row: {
          html: (dato) => {
            const nombre = dato?.nombre ?? '';
            const identificacion = dato?.identificacion ?? '';
            if (!nombre && !identificacion) return '';

            let html = `<div class="tabla-celda-col">`;
            if (nombre) {
              html += `<span class="tabla-principal">${escaparHtml(nombre)}</span>`;
            }
            if (identificacion && identificacion.trim() !== nombre.trim()) {
              html += `<span class="tabla-secundario">${escaparHtml(identificacion)}</span>`;
            }
            html += `</div>`;
            return html;
          },
          tooltip: (dato) => {
            const items: string[] = [];
            if (dato.nombre)
              items.push(
                `<div>Nombre: <strong>${escaparHtml(dato.nombre)}</strong></div>`,
              );
            if (dato.identificacion)
              items.push(
                `<div>Identificación: <strong>${escaparHtml(dato.identificacion)}</strong></div>`,
              );

            return items.length > 0 ? items.join('') : undefined;
          },
          noData: 'Sin nombre',
        },
      },

      //UniqueID, tipo, DevEUI y número de serie
      {
        header: {
          label: 'Dispositivo',
          sortable: false,
        },
        row: {
          html: (dato) => {
            const uniqueId = dato?.uniqueId ?? '';
            const marca = dato?.modelo?.marca ?? '';
            const modelo = dato?.modelo?.modelo ?? '';
            const tipo = dato?.tipo ?? '';

            const lineas: string[] = [];
            if (uniqueId) {
              lineas.push(
                this.linea(escaparHtml(uniqueId), 'fingerprint', 'tabla-codigo'),
              );
            }
            if (marca || modelo) {
              const textoMarcaModelo =
                marca && modelo ? `${marca} - ${modelo}` : marca || modelo;
              lineas.push(this.linea(escaparHtml(textoMarcaModelo), 'memory'));
            }
            if (tipo) {
              lineas.push(this.linea(escaparHtml(tipo), 'category'));
            }
            if (tipo === 'T1000-B' && dato.t1000b?.deveui) {
              lineas.push(
                this.linea(
                  escaparHtml(dato.t1000b.deveui),
                  'fingerprint',
                  'tabla-codigo',
                ),
              );
            }
            if (tipo === 'Qualcomm' && dato.qualcomm?.serialNumber) {
              lineas.push(
                `<span class="tabla-secundario">N.º de serie <span class="tabla-codigo">${escaparHtml(dato.qualcomm.serialNumber)}</span></span>`,
              );
            }
            if (!lineas.length) return '';
            return `<div class="tabla-celda-col">${lineas.join('')}</div>`;
          },
          tooltip: (dato) => {
            const items: string[] = [];
            if (dato.uniqueId) {
              items.push(
                `<div>ID único: <strong>${escaparHtml(dato.uniqueId)}</strong></div>`,
              );
            }
            if (dato.modelo?.marca && dato.modelo?.modelo) {
              const marca = dato.modelo.marca;
              const modelo = dato.modelo.modelo;
              const textoMarcaModelo =
                marca && modelo ? `${marca} - ${modelo}` : marca || modelo;
              items.push(
                `<div>Modelo: <strong>${escaparHtml(textoMarcaModelo)}</strong></div>`,
              );
            }
            if (dato.tipo) {
              items.push(
                `<div>Tipo: <strong>${escaparHtml(dato.tipo)}</strong></div>`,
              );
            }

            if (dato.tipo === 'T1000-B' && dato.t1000b?.deveui) {
              items.push(
                `<div>DevEUI: <strong>${escaparHtml(dato.t1000b.deveui)}</strong></div>`,
              );
            }
            if (dato.tipo === 'Qualcomm' && dato.qualcomm?.serialNumber) {
              items.push(
                `<div>Número de serie: <strong>${escaparHtml(dato.qualcomm.serialNumber)}</strong></div>`,
              );
            }
            return items.length > 0 ? items.join('') : undefined;
          },
          noData: 'Sin datos del dispositivo',
        },
      },

      // Servicios Contratados (íconos)
      {
        header: {
          label: 'Servicios',
        },
        row: {
          html: (dato) => this.getServiciosHtml(dato),
          tooltip: (dato) => {
            if (
              !dato.serviciosContratados ||
              dato.serviciosContratados.length === 0
            ) {
              return 'Sin servicios contratados';
            }
            const items = dato.serviciosContratados
              .map((s) => `<div>• ${escaparHtml(s.nombre)}</div>`)
              .join('');
            return `<div><strong>Servicios contratados:</strong></div>${items}`;
          },
          noData: 'Sin servicios',
        },
      },

      // Estado de la cuenta, con la fecha de alta debajo
      {
        header: {
          label: 'Estado de la cuenta',
        },
        row: {
          estado: (dato) => {
            if (!dato.estadoCuenta) return undefined;
            const habilitado = dato.estadoCuenta === 'Habilitado';
            const alta = fechaCelda(dato.fechaAlta);
            return {
              texto: dato.estadoCuenta,
              tono: habilitado ? 'ok' : 'error',
              icono: habilitado ? 'toggle_on' : 'block',
              detalle: alta ? `Alta ${alta.slice(0, 10)}` : 'Sin alta',
              tooltip: `Fecha de alta: ${alta || 'sin fecha'}`,
            };
          },
          noData: 'Sin estado de cuenta',
        },
      },

      {
        header: {
          label: 'Asignación',
        },
        row: {
          html: (dato) => {
            const activo = dato.activo;
            if (!activo) return '';

            const nombre = activo.identificacion ?? '';
            const categoria = activo.categoria ?? '';

            let icon = 'assistant_navigation';
            if (categoria === 'Vehículo') icon = 'directions_car';
            else if (categoria === 'Colectivo') icon = 'directions_bus';

            return this.linea(escaparHtml(nombre), icon, 'tabla-principal');
          },
          tooltip: (dato) => {
            if (!dato.activo) return 'Sin asignaciones';

            return `
        <div><strong>Nombre:</strong> ${escaparHtml(dato.activo.identificacion)}</div>
        <div><strong>Tipo:</strong> ${escaparHtml(dato.activo.categoria)}</div>
      `;
          },
          noData: 'Sin asignación',
        },
      },

      {
        header: {
          label: 'Última comunicación',
        },
        row: {
          html: (dato) => {
            // Los T1000-B suman su estado de radio ACÁ y no en una columna
            // aparte: sólo un tipo de tracker es LoRaWAN, así que una columna
            // propia quedaba vacía en todas las demás filas.
            //
            // Orden: comunicación (mismo criterio que el detalle, si hay
            // frecuencia), señal, tasa de entrega y por último la ubicación.
            const lora = this.htmlConectividadLora(dato);
            const bloques: string[] = [];
            const fecha = dato.ultimoReporte?.fechaCreacion;

            if (!fecha || dato.frecReporte) {
              bloques.push(
                `<span>${estadoHtml(estadoConexionCelda(fecha, dato.frecReporte))}</span>`,
              );
            }
            if (fecha) {
              bloques.push(
                `<span class="tabla-secundario">${fechaCelda(fecha)} · ${haceCelda(fecha)}</span>`,
              );
            }

            bloques.push(...lora);

            if (dato.ultimoReporte) {
              const valores = dato.ultimoReporte.valores;
              const lat = valores?.geojson?.coordinates?.[1];
              const lng = valores?.geojson?.coordinates?.[0];
              const velocidad = valores?.velocidad;
              // getOrientacion(null) devuelve 'Norte': sin este guard, un
              // reporte sin rumbo (los T1000-B no reportan ninguno) mostraba
              // " · N".
              const orientacion =
                valores?.orientacion != null
                  ? HelperService.getOrientacion(valores.orientacion)
                  : undefined;
              const direccion = orientacion
                ? this.orientacionAbreviada(orientacion)
                : '';

              if (lat != null && lng != null) {
                bloques.push(
                  this.linea(
                    `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                    'location_on',
                    'tabla-secundario',
                  ),
                );
              }
              if (velocidad != null || direccion) {
                bloques.push(
                  this.linea(
                    `${velocidad != null ? `${velocidad.toLocaleString('es-AR')} km/h` : ''}${
                      direccion ? ` · ${direccion}` : ''
                    }`,
                    'speed',
                    'tabla-secundario',
                  ),
                );
              }
            }

            return `<div class="tabla-celda-col">${bloques.join('')}</div>`;
          },
          tooltip: (dato) => {
            const lora = this.tooltipConectividadLora(dato);
            if (!dato.ultimoReporte) return lora || 'Sin comunicación';

            const valores = dato.ultimoReporte.valores;
            const lat = valores?.geojson?.coordinates?.[1];
            const lng = valores?.geojson?.coordinates?.[0];

            // Cada línea sólo si hay dato: los T1000-B no reportan velocidad ni
            // rumbo, y salían como "null km/h" y "Norte".
            const lineas: string[] = [
              `<div><strong>Último reporte:</strong> ${fechaCelda(dato.ultimoReporte.fechaCreacion)}</div>`,
            ];
            if (!dato.frecReporte) {
              lineas.push('<div>Sin frecuencia de reporte configurada</div>');
            }
            if (lat != null) {
              lineas.push(`<div><strong>Latitud:</strong> ${lat}</div>`);
            }
            if (lng != null) {
              lineas.push(`<div><strong>Longitud:</strong> ${lng}</div>`);
            }
            if (valores?.velocidad != null) {
              lineas.push(
                `<div><strong>Velocidad:</strong> ${valores.velocidad} km/h</div>`,
              );
            }
            if (valores?.orientacion != null) {
              lineas.push(
                `<div><strong>Dirección:</strong> ${HelperService.getOrientacion(
                  valores.orientacion,
                )}</div>`,
              );
            }

            const reporte = lineas.join('');
            return lora ? lora + reporte : reporte;
          },
          noData: 'Sin comunicación',
        },
      },
    ];

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        accionesGrouped: [
          //Detalles
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.detalles(dato),
            disabled: () =>
              !this.helper.tienePermiso('Administración - Ver trackers'),
          },
          //Editar
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            oculta() {
              return HelperService.getTipoCliente() === 'Final';
            },
            disabled: () =>
              !this.helper.tienePermiso('Administración - Editar trackers'),
          },

          //Eliminar
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            oculta() {
              return HelperService.getTipoCliente() === 'Final';
            },
            disabled: () =>
              !this.helper.tienePermiso('Administración - Eliminar trackers'),
          },

          {
            tipo: 'material',
            icon: 'more_vert',
            tooltip: 'Más acciones',
            children: [
              // Servicio Técnico
              {
                tipo: 'material',
                icon: 'build',
                tooltip: 'Servicio técnico',
                click: (dato) => this.crearEventoTecnico(dato),
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Servicio Técnico - Crear solicitudes',
                  ),
              },
              //Simular Tracker
              {
                tipo: 'material',
                icon: 'bug_report',
                tooltip: 'Simular tracker',
                click: (dato) => this.simularTracker(dato),
                oculta() {
                  return (
                    HelperService.getTipoCliente() === 'Final' ||
                    HelperService.getEnviroment() === 'prod'
                  );
                },
                disabled: () => !this.helper.esNivel0(), // TODO: cambiar permiso para simular tracker
              },
              //Crear Estado
              {
                tipo: 'material',
                icon: 'manage_accounts',
                tooltip: 'Cambiar estado de la cuenta',
                click: (dato) => this.crearEstadoEntidad(dato),
                oculta() {
                  return HelperService.getTipoCliente() === 'Final';
                },
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Activos - Cambiar estado de cuenta',
                  ), // TODO: cambiar permiso para cambiar estado de cuenta (tal vez)
              },
              // Actualizar imagenes
              {
                tipo: 'material',
                icon: 'add_photo_alternate',
                tooltip: 'Actualizar imágenes',
                click: (dato) => this.actualizarImagenes(dato),
                oculta: (dato) => !dato.fechaAlta,
                disabled: () =>
                  !this.helper.tienePermiso('Administración - Editar trackers'),
              },

              // Dar de Alta
              {
                tipo: 'material',
                icon: 'event_available',
                tooltip: 'Dar de alta',
                click: (dato) => this.darAlta(dato),
                oculta: (dato) => !!dato.fechaAlta,
                disabled: () =>
                  !this.helper.tienePermiso('Administración - Editar trackers'),
              },
            ],
          },

          // ── Network Server (ChirpStack) ──
          // Solo para T1000-B: es el único tipo de tracker que existe en el
          // Network Server. Mismas tres operaciones que en el listado de
          // dispositivos LoRaWAN, en un submenú para no saturar la fila.
          {
            tipo: 'material',
            icon: 'dns',
            tooltip: 'Network Server',
            oculta: (dato) => dato.tipo !== 'T1000-B',
            children: [
              {
                tipo: 'material',
                icon: 'search',
                tooltip: 'Consultar en Network Server',
                click: (dato) => this.consultarDeviceChirpstack(dato),
                disabled: () =>
                  !this.helper.tienePermiso('Administración - Ver trackers'),
              },
              {
                tipo: 'material',
                icon: 'add',
                tooltip: 'Crear en Network Server',
                click: (dato) => this.crearChirpstack(dato),
                disabled: () =>
                  !this.helper.tienePermiso('Administración - Crear trackers'),
              },
              {
                tipo: 'material',
                color: 'warn',
                icon: 'delete',
                tooltip: 'Eliminar en Network Server',
                click: (dato) => this.eliminarChirpstack(dato),
                disabled: () =>
                  !this.helper.tienePermiso(
                    'Administración - Eliminar trackers',
                  ),
              },
            ],
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await Promise.all([this.listar()]);
  }

  ngOnDestroy() {
    this.trackers$?.unsubscribe();
    this.reportes$?.unsubscribe();
  }
}
